<script setup lang="ts">
import type { ItemInfo } from "@/types"
import { useVirtualList } from "@vueuse/core"
import MatchItemRow from "@/components/FileImport/Match/MatchItemRow.vue"
import { ItemMatchStatus } from "@/types"

interface Props {
  items: ItemInfo[]
  options: string[]
  allowCreate?: boolean
  showInvalid?: boolean
  createdItems?: string[]
  disableDropdowns?: boolean
}

interface Emits {
  (event: "update", rowIndex: number, newValue: string): void
  (event: "create", rowIndex: number, newItemName: string): void
  (event: "ignore", rowIndex: number): void
  (event: "rename", rowIndex: number, newValue: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

function handleUpdate(rowIndex: number, newValue: string) {
  emit("update", rowIndex, newValue)
}

function handleCreate(rowIndex: number, newItemName: string) {
  emit("create", rowIndex, newItemName)
}

function handleIgnore(rowIndex: number) {
  emit("ignore", rowIndex)
}

function handleRename(rowIndex: number, newItemName: string) {
  emit("rename", rowIndex, newItemName)
}

function showItemAsInvalid(item: ItemInfo) {
  return (
    props.showInvalid &&
    !item.internalName &&
    item.status !== ItemMatchStatus.TO_IGNORE &&
    item.status !== ItemMatchStatus.ALREADY_IGNORED
  )
}

const { list, containerProps, wrapperProps } = useVirtualList(props.items, {
  itemHeight: 32,
})
</script>

<template>
  <div
    class="flex-1"
    v-bind="containerProps"
  >
    <div v-bind="wrapperProps">
      <table
        v-if="items"
        class="ph-no-capture table-auto rounded-md border text-sm"
      >
        <thead>
          <tr class="h-8">
            <th class="border border-gray-300 bg-gray-100 p-1 px-3 font-bold">
              In the file
            </th>
            <th class="border border-gray-300 bg-gray-100 p-1 px-3 font-bold">
              In Kaleidoscope
            </th>
            <th class="border border-gray-300 bg-gray-100 p-1 px-3 font-bold">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in list"
            :key="item.index"
            class="h-8"
          >
            <MatchItemRow
              :item="item.data"
              :options="options"
              :created-items="createdItems"
              :allow-create="allowCreate"
              :show-invalid="showItemAsInvalid(item.data)"
              :disable-dropdown="disableDropdowns"
              @update="handleUpdate(item.index, $event)"
              @create="handleCreate(item.index, $event)"
              @ignore="handleIgnore(item.index)"
              @rename="handleRename(item.index, $event)"
            />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
